import { useCallback, useState } from 'react'
import { AsyncValidationError } from '@dxp-module/common'
import { useIntl } from 'react-intl'
import { GraphQLError } from 'graphql'
import { IChangeEvent } from '@rjsf/core'
import { SignupFormData, SignupFormSchema } from '../model/SignupForm'
import signupFormDataTransformer from '../service/signupFormDataTransformer'

type ValidationReturn = {
  shouldValidate?: boolean
  hasErrors: boolean | undefined
  asyncErrors: AsyncValidationError | null
  handleValidationErrors: (data: IChangeEvent<SignupFormSchema>) => void
  handleAsyncValidationErrors: (error: readonly GraphQLError[]) => void
  resetAsyncErrors: () => void
  checkEmptyValues: (data: SignupFormSchema) => boolean | undefined
  hasUniqueEmailError: boolean
}

const useSignupFormValidate: (
  requiredFields?: string[]
) => ValidationReturn = requiredFields => {
  const intl = useIntl()
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(true)
  const [asyncErrors, setAsyncErrors] = useState<AsyncValidationError | null>(
    null
  )
  const [hasUniqueEmailError, setHasUniqueEmailError] = useState(false)
  const checkEmptyValues = useCallback(
    (data: SignupFormSchema) => {
      const formattedData: SignupFormData = signupFormDataTransformer.from(data)

      const requiredFormattedData =
        requiredFields?.map(
          key => formattedData[key as keyof SignupFormData]
        ) ?? []

      if (requiredFields) {
        const hasEmptyValues = requiredFormattedData.some(val =>
          typeof val === 'string' ? !val.trim() : !val
        )
        const hasDifferentLength =
          requiredFormattedData.length !== requiredFields.length
        return hasEmptyValues || hasDifferentLength
      }
    },
    [requiredFields]
  )

  const handleValidationErrors = useCallback(
    (data: IChangeEvent<SignupFormSchema>) => {
      const hasValidationErrors = data.errors?.length > 0
      const hasEmptyValues = checkEmptyValues(data.formData)
      setHasErrors(hasValidationErrors || hasEmptyValues)
      setHasUniqueEmailError(false)
    },
    [setHasErrors, hasErrors, asyncErrors, requiredFields]
  )

  const handleAsyncValidationErrors = useCallback(
    // @TODO update type, after completing onsubmit functionality
    /*eslint-disable */
    (data: any) => {
      const err: AsyncValidationError = signupFormDataTransformer.fromOld(
        data.errors,
        intl
      )
      if (err?.nameInfo?.uniqueUserEmail) {
        setHasUniqueEmailError(true)
        delete err.nameInfo.uniqueUserEmail
      }
      setAsyncErrors(err)
    },
    [asyncErrors]
  )

  const resetAsyncErrors = useCallback(() => {
    setAsyncErrors(null)
  }, [])

  return {
    hasErrors,
    asyncErrors,
    handleValidationErrors,
    handleAsyncValidationErrors,
    resetAsyncErrors,
    checkEmptyValues,
    hasUniqueEmailError,
  }
}

export default useSignupFormValidate
